/**
 * Shared "enroll a payer for every transaction type it requires" loop.
 *
 * Extracted from three near-identical call sites (admin org billing, the
 * provider enrollments tab, and the setup wizard) which duplicated this loop
 * and — critically — dropped a per-transaction failure whenever at least one
 * other transaction for the same payer succeeded. Callers get back exactly
 * which transaction types succeeded and which failed so they can report
 * partial failures instead of a false "all clear" toast.
 */

import { useCallback, useState } from 'react';
import type { PayerResponse } from '@store/generated/payers';

import { getEnrollableTransactionTypes } from '../utils/enrollmentStatus';

export interface EnrollAllResult {
  /** Transaction types that had no enrollment attempted (payer required none). */
  requested: string[];
  succeeded: string[];
  failed: string[];
  /** Parallel to `failed` — the error for each failed transaction type. */
  errors: unknown[];
}

export function useEnrollAllRequiredTransactions() {
  const [enrollingPayerId, setEnrollingPayerId] = useState<string | null>(null);

  const enrollAllRequiredTransactions = useCallback(
    async (
      payer: PayerResponse,
      enrollOne: (transactionType: string) => Promise<unknown>,
    ): Promise<EnrollAllResult> => {
      const requested = getEnrollableTransactionTypes(payer);
      if (!payer.id || requested.length === 0) {
        return { requested, succeeded: [], failed: [], errors: [] };
      }

      setEnrollingPayerId(payer.id);
      const succeeded: string[] = [];
      const failed: string[] = [];
      const errors: unknown[] = [];
      for (const transactionType of requested) {
        try {
          await enrollOne(transactionType);
          succeeded.push(transactionType);
        } catch (error) {
          failed.push(transactionType);
          errors.push(error);
        }
      }
      setEnrollingPayerId(null);

      return { requested, succeeded, failed, errors };
    },
    [],
  );

  return { enrollAllRequiredTransactions, enrollingPayerId };
}
